import { io } from 'socket.io-client';
import { ENABLE_DEBUG_LOGS, STSUI_WS_URL } from '../utils/config';
class StsuiWebSocketService {
    constructor() {
        this.socket = null;
        this.connectionState = 'disconnected';
        this.roomCallbacks = new Map();
        this.connectionStatusCallbacks = new Set();
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.isUserActive = true;
        this.isTabVisible = true;
        this.activityTimer = null;
        this.heartbeatInterval = null;
        this.heartbeatTimeout = null;
        this.setupActivityDetection();
        this.setupVisibilityDetection();
    }
    setupActivityDetection() {
        const resetTimer = () => {
            this.isUserActive = true;
            this.checkConnectionState();
            if (this.activityTimer)
                clearTimeout(this.activityTimer);
            this.activityTimer = setTimeout(() => {
                this.isUserActive = false;
                this.checkConnectionState();
            }, 5 * 60 * 1000);
        };
        ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'].forEach((event) => {
            document.addEventListener(event, resetTimer, true);
        });
        resetTimer();
    }
    setupVisibilityDetection() {
        document.addEventListener('visibilitychange', () => {
            this.isTabVisible = !document.hidden;
            this.checkConnectionState();
        });
    }
    checkConnectionState() {
        const shouldBeConnected = this.isTabVisible && this.isUserActive;
        if (shouldBeConnected && this.connectionState === 'disconnected') {
            this.connect();
        }
        else if (!shouldBeConnected && this.connectionState === 'connected') {
            this.disconnect();
        }
    }
    notifyConnectionStatusChange() {
        this.connectionStatusCallbacks.forEach((callback) => {
            try {
                callback();
            }
            catch (error) {
                console.error('Error in stSui connection status callback:', error);
            }
        });
    }
    saveToCache(room, data) {
        const cacheData = {
            data: data,
            serverTimestamp: Date.now(),
            cachedAt: Date.now(),
            lastUpdated: Date.now(),
        };
        localStorage.setItem(`stsui-ws-room-${room}`, JSON.stringify(cacheData));
    }
    getCachedData(room) {
        const cached = localStorage.getItem(`stsui-ws-room-${room}`);
        if (!cached)
            return null;
        try {
            const { data, serverTimestamp } = JSON.parse(cached);
            if (!data || typeof serverTimestamp !== 'number') {
                localStorage.removeItem(`stsui-ws-room-${room}`);
                return null;
            }
            const dataAge = Date.now() - serverTimestamp;
            const isStale = dataAge > 60000;
            return {
                data,
                serverTimestamp,
                isStale,
            };
        }
        catch (error) {
            console.error('Error parsing cached stSui data:', error);
            localStorage.removeItem(`stsui-ws-room-${room}`);
            return null;
        }
    }
    connect() {
        var _a;
        if ((_a = this.socket) === null || _a === void 0 ? void 0 : _a.connected) {
            console.log('stSui WebSocket already connected');
            return;
        }
        if (this.connectionState === 'connecting' || this.connectionState === 'reconnecting') {
            console.log('stSui WebSocket connection already in progress');
            return;
        }
        this.connectionState = 'connecting';
        this.notifyConnectionStatusChange();
        console.log(`Attempting stSui WebSocket connection to: ${STSUI_WS_URL}`);
        try {
            this.socket = io(STSUI_WS_URL, {
                transports: ['websocket'],
                timeout: 30000,
                reconnectionDelay: 2000,
                reconnectionAttempts: this.maxReconnectAttempts,
                autoConnect: true,
            });
            this.setupSocketHandlers();
        }
        catch (error) {
            console.error('Failed to create stSui WebSocket:', error);
            this.connectionState = 'disconnected';
            this.notifyConnectionStatusChange();
        }
    }
    setupSocketHandlers() {
        if (!this.socket)
            return;
        this.socket.on('connect', () => {
            var _a;
            console.log('stSui WebSocket connected successfully', (_a = this.socket) === null || _a === void 0 ? void 0 : _a.id);
            this.connectionState = 'connected';
            this.reconnectAttempts = 0;
            this.notifyConnectionStatusChange();
            this.startHeartbeat();
            this.roomCallbacks.forEach((callbacks, room) => {
                if (callbacks.size > 0) {
                    this.sendSubscription(room);
                }
            });
        });
        this.socket.on('disconnect', (reason) => {
            console.log(`stSui WebSocket disconnected. Reason: ${reason}`);
            this.connectionState = 'disconnected';
            this.notifyConnectionStatusChange();
            this.stopHeartbeat();
            if (this.isTabVisible &&
                this.isUserActive &&
                this.reconnectAttempts < this.maxReconnectAttempts) {
                this.reconnectAttempts++;
                this.connectionState = 'reconnecting';
                console.log(`Reconnecting stSui WebSocket (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
            }
        });
        this.socket.on('connect_error', (error) => {
            console.error('stSui WebSocket connect_error:', error);
            this.connectionState = 'disconnected';
            this.notifyConnectionStatusChange();
            this.stopHeartbeat();
        });
        this.socket.on('update', (payload) => {
            // Fires on every server broadcast — gated so it doesn't flood the console.
            if (ENABLE_DEBUG_LOGS)
                console.log('stSui WebSocket received update:', payload);
            this.roomCallbacks.forEach((callbacks, room) => {
                this.saveToCache(room, payload);
                callbacks.forEach((callback) => {
                    try {
                        callback(payload);
                    }
                    catch (error) {
                        console.error(`Error in stSui subscriber callback for room ${room}:`, error);
                    }
                });
            });
        });
        this.socket.on('error', (error) => {
            console.error('stSui WebSocket error:', error);
        });
    }
    startHeartbeat() {
        var _a;
        this.stopHeartbeat();
        this.heartbeatInterval = setInterval(() => {
            var _a;
            if (this.connectionState === 'connected' && ((_a = this.socket) === null || _a === void 0 ? void 0 : _a.connected)) {
                this.socket.emit('ping');
                this.heartbeatTimeout = setTimeout(() => {
                    console.warn('stSui WebSocket heartbeat timeout, reconnecting...');
                    this.disconnect();
                    this.connect();
                }, 10000);
            }
        }, 30000);
        (_a = this.socket) === null || _a === void 0 ? void 0 : _a.on('pong', () => {
            if (this.heartbeatTimeout) {
                clearTimeout(this.heartbeatTimeout);
                this.heartbeatTimeout = null;
            }
        });
    }
    stopHeartbeat() {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
        if (this.heartbeatTimeout) {
            clearTimeout(this.heartbeatTimeout);
            this.heartbeatTimeout = null;
        }
    }
    disconnect() {
        if (this.socket) {
            this.stopHeartbeat();
            this.socket.disconnect();
            this.socket = null;
            this.connectionState = 'disconnected';
            this.notifyConnectionStatusChange();
        }
    }
    subscribe(room, callback) {
        var _a;
        if (!this.roomCallbacks.has(room)) {
            this.roomCallbacks.set(room, new Set());
            if ((_a = this.socket) === null || _a === void 0 ? void 0 : _a.connected) {
                this.sendSubscription(room);
            }
        }
        this.roomCallbacks.get(room).add(callback);
        if (!this.socket && this.isTabVisible && this.isUserActive) {
            setTimeout(() => {
                if (!this.socket && this.isTabVisible && this.isUserActive) {
                    this.connect();
                }
            }, 100);
        }
        // Return cached data immediately
        const cachedData = this.getCachedData(room);
        if (cachedData) {
            try {
                callback(cachedData.data);
            }
            catch (error) {
                console.error(`Error calling callback with cached data for room ${room}:`, error);
            }
        }
    }
    unsubscribe(room, callback) {
        var _a;
        if (this.roomCallbacks.has(room)) {
            this.roomCallbacks.get(room).delete(callback);
            if (this.roomCallbacks.get(room).size === 0) {
                this.roomCallbacks.delete(room);
                if ((_a = this.socket) === null || _a === void 0 ? void 0 : _a.connected) {
                    this.sendUnsubscription(room);
                }
            }
        }
    }
    sendSubscription(room) {
        var _a;
        if ((_a = this.socket) === null || _a === void 0 ? void 0 : _a.connected) {
            console.log(`Subscribing to stSui room: ${room}`);
            this.socket.emit('subscribe', room);
        }
    }
    sendUnsubscription(room) {
        var _a;
        if ((_a = this.socket) === null || _a === void 0 ? void 0 : _a.connected) {
            console.log(`Unsubscribing from stSui room: ${room}`);
            this.socket.emit('unsubscribe', room);
        }
    }
    getConnectionStatus() {
        return this.connectionState;
    }
    getActivityStatus() {
        return {
            isUserActive: this.isUserActive,
            isTabVisible: this.isTabVisible,
        };
    }
    onConnectionStatusChange(callback) {
        this.connectionStatusCallbacks.add(callback);
        return () => {
            this.connectionStatusCallbacks.delete(callback);
        };
    }
}
export const stsuiWebSocketService = new StsuiWebSocketService();
